import { useCallback, useMemo, useState } from "react";
import {
	convertLiveSessionsToPendingSessions,
	type EditQuizAssessmentUUids,
	getDefaultLiveSessionTimes,
	getLiveSessionErrors,
	getLiveSessionsCancelledByUpdate,
	type LiveSessionWithInvitations,
	type PendingLiveSession,
	type ProctorGroupInfo,
	type ProctorInfo,
	resetStartedLiveSessions,
} from "../../live/liveHelper";
import { coreToast } from "../../../core";
import { UseEditQuizStateProps } from "./useEditQuizState";
import i18nPackage from "../../../i18n/i18n";

type PendingLiveSessionUpdate = Pick<
	PendingLiveSession,
	"name" | "startTime" | "endTime" | "capacity" | "registrationStartTime" | "registrationEndTime"
>;

type UseLiveStateProps = Pick<
	UseEditQuizStateProps,
	| "api"
	| "quizInfo"
	| "existingLiveSessions"
	| "setExistingLiveSessions"
	| "pendingLiveSessions"
	| "setPendingLiveSessions"
	| "pendingLiveSessionIds"
	| "setPendingLiveSessionIds"
	| "currFakeLiveSessionId"
	| "initialLiveSessions"
>;

/**Anything to do with live state */
export default function useLiveState({
	api,
	quizInfo,
	existingLiveSessions,
	setExistingLiveSessions,
	pendingLiveSessions,
	setPendingLiveSessions,
	pendingLiveSessionIds,
	setPendingLiveSessionIds,
	currFakeLiveSessionId,
	initialLiveSessions,
}: UseLiveStateProps) {
	const { getCourseInstructors, getInstituteProctorGroups, getAssessmentLiveSessions, getLiveSessionInvitations } = api;

	const [availableProctors, setAvailableProctors] = useState<Record<string, ProctorInfo>>({});
	const [availableProctorGroups, setAvailableProctorGroups] = useState<Record<number, ProctorGroupInfo>>({});
	const [liveSessionsNextConfirmationMessage, setLiveSessionsNextConfirmationMessage] = useState<string | undefined>();

	const getNextFakeLiveSessionId = () => `#${currFakeLiveSessionId.current++}`;

	const validateLiveSession = useCallback(
		({ showToast = true }: { showToast?: boolean } = {}) => {
			const liveSessionErrors = getLiveSessionErrors(existingLiveSessions, pendingLiveSessions, availableProctorGroups);
			const cancelledLiveSessions = getLiveSessionsCancelledByUpdate(
				existingLiveSessions,
				pendingLiveSessions,
				availableProctorGroups,
			);
			if (Object.values(liveSessionErrors).some((errors) => errors.length > 0)) {
				if (showToast) {
					const sessionNamesList = Object.entries(liveSessionErrors)
						.filter(([, errors]) => errors.length > 0)
						.map(([id]) => pendingLiveSessions[id]?.name || "[Unnamed]");
					let sessionNames: string;
					if (sessionNamesList.length === 1) {
						[sessionNames] = sessionNamesList;
					} else {
						sessionNames = `${sessionNamesList.slice(0, -1).join(", ")} and ${sessionNamesList[sessionNamesList.length - 1]}`;
					}
					coreToast.error(
						Object.values(liveSessionErrors).some((errors) => errors.includes("noSessions"))
							? i18nPackage.t("editQuizPage.liveSessionStep.error.noLiveSessionMessage")
							: i18nPackage.t("editQuizPage.liveSessionStep.error.sessionIssueMessage", { sessionNames }),
						{ containerId: `toast-${quizInfo.quizId}` },
					);
				}
				return false;
			}
			if (cancelledLiveSessions.length > 0 && showToast) {
				setLiveSessionsNextConfirmationMessage(
					i18nPackage.t("editQuizPage.liveSessionStep.error.sessionWarningMessage", {
						cancelledSessionNames: cancelledLiveSessions.map(({ name }) => name).join(", "),
					}),
				);
			}
			return true;
		},
		[existingLiveSessions, pendingLiveSessions, availableProctorGroups],
	);

	const fetchAndSetExistingLiveSessions = useCallback(
		async (assessmentUuids: EditQuizAssessmentUUids | undefined): Promise<LiveSessionWithInvitations[]> => {
			if (!assessmentUuids) {
				throw new Error("no assessmentUuids were obtained");
			}
			if (!quizInfo.courseId) {
				throw new Error("no course ID");
			}

			const instructors = await getCourseInstructors(quizInfo.courseId);
			setAvailableProctors(Object.fromEntries(instructors.map(({ uuid, name }) => [uuid, { id: uuid, name }])));
			setAvailableProctorGroups(
				Object.fromEntries(
					(await getInstituteProctorGroups(assessmentUuids.instituteUuid)).map((proctorGroup) => [
						proctorGroup.id,
						{ ...proctorGroup, autoCancelEmptyHoursBefore: proctorGroup.autoCancelEmptyHoursBefore ?? undefined },
					]),
				),
			);

			/**
			 * An assessment uuid only exists once the assessment does, so without one there is normally nowhere
			 * to read a schedule from and the empty list is the honest answer. `initialLiveSessions` is the
			 * exception: a caller whose saved schedule travels beside it rather than living in an assessment
			 * hands it over here, so that from this line down the schedule is treated exactly like a fetched
			 * one — same invitation lookup, same `existingLiveSessions`, same conversion to pending sessions.
			 */
			const sessions = assessmentUuids.assessmentUuid
				? (
						await getAssessmentLiveSessions({
							...assessmentUuids,
							assessmentUuid: assessmentUuids.assessmentUuid,
						})
					).results
				: (initialLiveSessions ?? []);
			const invitations = Object.fromEntries(
				(
					await Promise.allSettled(
						sessions.map(async ({ id }) => [
							id,
							(await getLiveSessionInvitations(id)).filter(({ state }) => !state.cancelledAt),
						]),
					)
				)
					.filter((result) => result.status === "fulfilled")
					.map(
						(result) =>
							(
								result as PromiseFulfilledResult<
									[
										string,
										{
											liveSessionId: string;
											proctorGroupId: number;
										}[],
									]
								>
							).value,
					),
			);
			const sessionsWithInvitations = sessions.map((session) => ({
				...session,
				invitations: invitations[session.id] ?? [],
			}));
			setExistingLiveSessions(Object.fromEntries(sessionsWithInvitations.map((session) => [session.id, session])));
			return sessionsWithInvitations;
		},
		[
			getAssessmentLiveSessions,
			getCourseInstructors,
			getInstituteProctorGroups,
			getLiveSessionInvitations,
			initialLiveSessions,
			quizInfo.courseId,
			setAvailableProctorGroups,
			setAvailableProctors,
			setExistingLiveSessions,
		],
	);

	const fetchAndSetLiveSessions = useCallback(
		async (assessmentUuids: Parameters<typeof fetchAndSetExistingLiveSessions>[0]) => {
			// let this still run to populate available proctors
			const sessions = await fetchAndSetExistingLiveSessions(assessmentUuids);
			// Without a quiz id the fetch above found nothing, so overwriting the pending set here would
			// discard whatever `refreshSettings` restored into it for an assessment the LMS has not created
			// yet. A caller that supplied a schedule is the one case where there *is* something to seed from,
			// and leaving it out is what left its sessions invisible and Save disabled on `noSessions`.
			if (!quizInfo.quizId && !initialLiveSessions?.length) return;

			setPendingLiveSessionIds(sessions.map(({ id }) => id));
			setPendingLiveSessions(convertLiveSessionsToPendingSessions(sessions));
		},
		[fetchAndSetExistingLiveSessions, initialLiveSessions, setPendingLiveSessionIds, setPendingLiveSessions],
	);

	const getNextSessionNumber = useCallback(() => {
		const regex = /^Session (\d+)\s*(?:\(Demo\))?\s*$/;
		return (
			Math.max.apply(
				null,
				Object.values(pendingLiveSessions)
					.map(({ name }) => {
						const result = regex.exec(name);
						return result ? Number(result[1]) : 0;
					})
					.concat([0]),
			) + 1
		);
	}, [pendingLiveSessions]);

	const addPendingSession = useCallback(() => {
		const identifier = getNextFakeLiveSessionId();
		const { startTime, endTime } = getDefaultLiveSessionTimes(quizInfo);

		setPendingLiveSessionIds((prevIds) => prevIds.concat([identifier]));
		setPendingLiveSessions((prevSessions) => ({
			...prevSessions,
			[identifier]: {
				identifier,
				name: i18nPackage.t("editQuizPage.liveSessionStep.defaultName", { number: getNextSessionNumber() }),
				startTime,
				endTime,
				capacity: 50,
				proctorId: Object.values(availableProctors)[0]?.id,
			},
		}));
		return identifier;
	}, [
		availableProctors,
		getNextSessionNumber,
		getNextFakeLiveSessionId,
		setPendingLiveSessionIds,
		setPendingLiveSessions,
		quizInfo,
	]);

	const updatePendingSession = useCallback(
		(id: string, update: PendingLiveSessionUpdate) => {
			setPendingLiveSessions(
				Object.fromEntries(
					Object.values(pendingLiveSessions).map((session) => [
						session.identifier,
						session.identifier === id ? { ...session, ...update } : session,
					]),
				),
			);
		},
		[pendingLiveSessions],
	);

	const removePendingSession = useCallback(
		(id: string) => {
			setPendingLiveSessionIds(pendingLiveSessionIds.filter((sessionId) => sessionId !== id));
			setPendingLiveSessions(
				Object.fromEntries(Object.entries(pendingLiveSessions).filter(([sessionId]) => sessionId !== id)),
			);
		},
		[pendingLiveSessionIds, pendingLiveSessions],
	);

	const syncStartedSessions = useCallback(() => {
		setPendingLiveSessions((prev) => {
			const next = resetStartedLiveSessions(existingLiveSessions, prev);
			// Only update state if something actually changed to avoid infinite loops
			return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
		});
	}, [existingLiveSessions]);

	const resetConfirmationMessage = useCallback(() => {
		setLiveSessionsNextConfirmationMessage(undefined);
	}, []);

	const pendingLiveSessionsArray = useMemo(
		() => pendingLiveSessionIds.map((id) => pendingLiveSessions[id]),
		[pendingLiveSessionIds, pendingLiveSessions],
	);

	return {
		state: {
			pendingLiveSessionIds,
			existingLiveSessions,
			pendingLiveSessions,
			pendingLiveSessionsArray,
			availableProctors,
			availableProctorGroups,
			liveSessionsNextConfirmationMessage,
		},
		action: {
			validateLiveSession,
			syncStartedSessions,
			fetchAndSetLiveSessions,
			fetchAndSetExistingLiveSessions,
			resetConfirmationMessage,
			updatePendingSession,
			removePendingSession,
			addPendingSession,
		},
	};
}
